import React, { FC, ReactNode, useState } from 'react'

import { ChevronDownIcon, Loader2Icon } from 'lucide-react'

import { Card, CardAction, CardContent, CardHeader, CardTitle } from '../../../../../ui/card'
import { Button } from '../../../../../ui/button'
import { IconCreate } from '../../../../../icons/IconCreate'

import { cn } from '../../../../../../lib/utils'

interface CardWrapperProps {
  title: string | ReactNode
  className?: string
  isCollapsible?: boolean
  titleClassName?: string
  children: ReactNode
  onActionClick?: () => void
  actionIcon?: ReactNode
  isEditable?: boolean
  isEdit?: boolean
  setIsEdit?: (state: boolean) => void
  onSaveEdit?: () => Promise<void>
  onCancelEdit?: () => void
}
export const CardWrapper: FC<CardWrapperProps> = ({
  title,
  className,
  isCollapsible,
  titleClassName,
  onActionClick,
  actionIcon,
  isEditable,
  isEdit,
  setIsEdit,
  onSaveEdit,
  onCancelEdit,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState(false)

  return (
    <Card className={cn('shadow-none', className)}>
      <CardHeader
        className={cn('flex-row flex items-center justify-between gap-2 px-6 py-3', titleClassName)}
      >
        <CardTitle className="text-base font-medium">{title}</CardTitle>
        <CardAction className="!mt-0 flex items-center gap-4">
          {onSaveEdit && (
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
          )}
          {isEditable && (
            <>
              {isEdit ? (
                <>
                  <Button
                    variant="link"
                    type="button"
                    className="py-[6px] leading-none"
                    onClick={onCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={loading}
                    variant="red"
                    type="button"
                    className="py-[6px] px-6 rounded-full text-xs h-auto"
                    onClick={async () => {
                      setLoading(true)
                      if (onSaveEdit) await onSaveEdit()
                      setLoading(false)
                    }}
                  >
                    {loading && <Loader2Icon className="animate-spin" />}
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  variant="link"
                  type="button"
                  className="rounded-full p-0 group"
                  onClick={() => setIsEdit && setIsEdit(true)}
                >
                  <IconCreate className="!w-5 !h-auto group-hover:fill-SeabiscuitMainThemeColor" />
                </Button>
              )}
            </>
          )}
          {isCollapsible && (
            <Button
              variant="link"
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                'rounded-full transition-all duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0',
                isOpen
                  ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                  : 'text-black bg-black/5'
              )}
            >
              <ChevronDownIcon className="size-4" />
            </Button>
          )}
        </CardAction>
      </CardHeader>
      {isOpen && (
        <CardContent className="pb-2">
          <ul className="divide-y divide-y-black/10">{children}</ul>
        </CardContent>
      )}
    </Card>
  )
}
